import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Button from "../../common/button";
import { Link } from "react-router-dom";
import {
  useLazyGetAcademyCourseDetailsQuery,
  useLazyGetAcademyCourseModuleDetailsQuery,
  useLazyGetAcademyHomeDataQuery,
  useLazyGetCourseRewardsQuery,
} from "../../../store/api/academyApi";
import { useDispatch } from "react-redux";
import { setDropdownList, setPage } from "../../../store/slices/academySlice";
import { Course } from "../courses";
import { DropdownList } from "../academy-home";
import {
  AcademyStarCompletedIcon,
  BackButtonIcon,
  BadgeIcon,
  DownButtonIcon,
  ProgressCircularBar,
} from "../../../icons/academy-icons";
import { openModal } from "../../../store/slices/modalSlice";
import BecomeMember from "../../common/membership";
import { useAppSelector } from "../../../hooks/store-hooks";
import { MODAL_NAME } from "../../../constants";
import { getLocalStorage, setLocalStorage } from "../../../helpers/storage";
import { ROUTES } from "../../../constants/routes";
import { formatDuration } from "../../../helpers";
import { trackEvent } from "../../../analytics";

enum CompletionStatus {
  START = "Start",
  RESUME = "Continue",
  COMPLETED = "Completed",
}

interface Reward {
  module_id: number;
  badge_icon: string;
  is_completed: boolean;
}

interface RewardsComponentProps {
  rewards: Reward[];
}

interface Episode {
  episode_id: number;
  episode_number: number;
  title: string;
  duration: number;
  completion_status: string;
  badge_icon: string;
  is_subscribed?: boolean;
  progress_bar: number;
}

interface Module {
  module_id: string;
  title: string;
  duration: number;
  episodes_count: number;
  progress_bar: {
    completion_percent: number;
    badge_icon: string;
  };
  thumbnail: string;
  thumbnail_compressed: string;
  button: string;
}

const CourseComponent: React.FC = () => {
  const { course_id: courseId } = useParams();
  const [courseTitle, setCourseTitle] = useState<string>("");
  const [modules, setModules] = useState<Module[]>([]);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [triggerGetAcademyCourseDetailsQueryData] =
    useLazyGetAcademyCourseDetailsQuery();
  const [triggerGetAcademyCourseModuleDetailsQueryData] =
    useLazyGetAcademyCourseModuleDetailsQuery();
  const [triggerGetAcademyAllTabData] = useLazyGetAcademyHomeDataQuery();
  const [triggerGetRewards] = useLazyGetCourseRewardsQuery();
  const [rewards, setRewards] = useState<any>([]);

  const [newlyAddedBadge, setNewlyAddedBadge] = useState<{
    module_id: string;
    badge_icon: string;
  } | null>(null);
  const [showCongratulations, setShowCongratulations] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getRewardData = async () => {
    const resp = await triggerGetRewards({ courseId }).unwrap();
    const moduleBadges = getLocalStorage("module_badges");
    if (moduleBadges) {
      const parsedModuleBadges = JSON.parse(moduleBadges);
      if (parsedModuleBadges.courseId === courseId) {
        const existingBadges = parsedModuleBadges.moduleBadges;
        const newBadges = resp?.data;

        newBadges.forEach((newBadge: any) => {
          const existingBadge = existingBadges.find(
            (badge: any) => badge.module_id === newBadge.module_id,
          );

          if (
            existingBadge &&
            existingBadge.is_completed !== newBadge.is_completed
          ) {
            setNewlyAddedBadge(newBadge);
            setShowCongratulations(true);
          }
        });
      }
    }
    if (resp?.success) {
      setLocalStorage(
        "module_badges",
        JSON.stringify({ courseId, moduleBadges: resp.data }),
      );
      setRewards(resp.data);
    }
  };

  const getAllCourseData = async () => {
    const resp = await triggerGetAcademyAllTabData({
      type: "course",
    }).unwrap();
    if (resp?.success) {
      dispatch(setPage("course"));
      const dropdown_list = resp.data?.courses_list?.map((course: Course) => {
        if (course.course_id === courseId) {
          setCourseTitle(course.title);
        }
        return {
          title: course.title,
          id: course.course_id,
        };
      });
      const dropdownList = [...DropdownList];
      dropdownList[1] = { ...dropdownList[1], dropdown: dropdown_list };
      dispatch(setDropdownList(dropdownList));
    }
  };

  useEffect(() => {
    Promise.allSettled([getAllCourseData(), getCourseData(), getRewardData()]);
  }, [courseId]);

  const [openModuleId, setOpenModuleId] = useState<string>("");

  const handleToggleModule = (moduleId: string) => {
    const isOpening = openModuleId !== moduleId;
    setOpenModuleId(openModuleId === moduleId ? "" : moduleId);
    if (isOpening) {
      // Track event when a module is opened to show episode list
      trackEvent("course_module_episode_list_opened", {
        course_id: courseId,
        course_title: courseTitle,
        module_id: moduleId,
        module_title: modules.find((m) => m.module_id === moduleId)?.title,
      });
    }
  };

  const getCourseData = async () => {
    const response = await triggerGetAcademyCourseDetailsQueryData({
      courseId,
    })?.unwrap();
    if (response?.success) {
      setModules(response.data.modules);
    }
  };

  const getModuleData = async () => {
    const response = await triggerGetAcademyCourseModuleDetailsQueryData({
      moduleId: openModuleId,
    })?.unwrap();
    if (response?.success) {
      setEpisodes(response.data.episodes);
    }
  };

  useEffect(() => {
    if (openModuleId === "") return;
    getModuleData();
  }, [openModuleId]);

  const renderEpisodes = (
    episode: Episode,
    module_id: string,
    badge_icon: string,
  ) => {
    return (
      <div className="flex justify-between px-6 py-4 items-center border-b-[1px]">
        <div className="flex items-center gap-8 relative">
          <div className="absolute -left-[56px]">
            <ProgressCircularBar
              radius={7}
              completion_percentage={episode.progress_bar}
            />
          </div>
          {episode.completion_status === "COMPLETED" ? (
            <div className="relative -left-[7px] -top-[2px] w-14 h-14 overflow-hidden">
              <AcademyStarCompletedIcon />
            </div>
          ) : (
            <img
              src={badge_icon}
              className={`w-12 h-12 bg-[#EBEBEB] shadow-black drop-shadow-md p-1 rounded-full ${episode.completion_status === "START" ? "grayscale" : ""}`}
            />
          )}

          <Link
            to={`/academy/courses/${courseId}/${module_id}/${episode.episode_id}`}
            onClick={() => {
              trackEvent("course_module_episode_list_cta_clicked", {
                course_id: courseId,
                course_title: courseTitle,
                module_id: module_id,
                module_title: modules.find((m) => m.module_id === module_id)
                  ?.title,
                episode_id: episode.episode_id,
                episode_title: episode.title,
              });
              trackEvent("course_module_episode_opened", {
                course_id: courseId,
                course_title: courseTitle,
                module_id: module_id,
                module_title: modules.find((m) => m.module_id === module_id)
                  ?.title,
                episode_id: episode.episode_id,
                episode_title: episode.title,
              });
            }}
          >
            <h2 className="text-xl font-bold">
              {episode.episode_number + ". " + episode.title}
            </h2>
            <h4 className="text-lg font-light">
              Duration: {Math.floor(episode.duration / 60)}m{" "}
              {Math.ceil(episode.duration % 60)}s
            </h4>
          </Link>
        </div>
        {episode.is_subscribed ? (
          <Link
            to={`/academy/courses/${courseId}/${module_id}/${episode.episode_id}`}
          >
            {episode.completion_status !== "COMPLETED" && (
              <Button customClasses="h-fit font-bold">
                {
                  CompletionStatus[
                    episode.completion_status as keyof typeof CompletionStatus
                  ]
                }
              </Button>
            )}
          </Link>
        ) : (
          <Button
            customClasses="h-fit font-bold"
            onClick={() => {
              dispatch(openModal({ modalName: "BECOME_MEMBER" }));
            }}
          >
            {episode.completion_status}
          </Button>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (showCongratulations && newlyAddedBadge) {
      navigate(ROUTES.ACADEMY_MODULE_COMPLETED, {
        state: { badges: rewards, newlyAddedBadge },
      });
      setShowCongratulations(false);
    }
  }, [showCongratulations, newlyAddedBadge, rewards, navigate]);

  const gridOverlayStyle = {
    background: "linear-gradient(263deg, rgba(0, 0, 0, 0) 1.32%, #000000 100%)",
  };
  return (
    <div className="flex">
      <div className="flex flex-col gap-4 w-3/4 p-2">
        <div className="flex items-center h-fit gap-2 mb-4">
          <Link to="/academy/courses">
            <BackButtonIcon width={20} height={16} />
          </Link>
          <h1 className="text-[34px] font-bold">{courseTitle}</h1>
        </div>
        {modules.map((module, idx) => {
          return module.module_id !== openModuleId ? (
            <div
              className="p-4 bg-white rounded-lg flex justify-between items-center cursor-pointer"
              onClick={() => {
                handleToggleModule(module.module_id);
                trackEvent("course_module_list_cta_clicked", {
                  course_id: courseId,
                  course_title: courseTitle,
                  module_id: module.module_id,
                  module_title: module?.title,
                  cta_for: "module_card",
                });
              }}
            >
              <div className="flex gap-4">
                <img
                  src={module.thumbnail_compressed}
                  alt="Course"
                  className="rounded-lg w-[44] h-[105px]"
                />
                <div>
                  <h2 className="text-[22px] font-semibold">{module.title}</h2>
                  <h4 className="font-light text-gray-700">
                    Module {idx + 1} • {module.episodes_count} episodes •{" "}
                    {Math.floor(module.duration / 60) === 0
                      ? `${module.duration % 60}m`
                      : `${Math.floor(module.duration / 60)}h ${module.duration % 60}m`}
                  </h4>
                  <img
                    src={module.progress_bar.badge_icon}
                    className={`w-10 h-10 mt-2 ${module.button !== "COMPLETED" ? "grayscale" : ""}`}
                  />
                </div>
              </div>
              <div className="cursor-pointer">
                <DownButtonIcon width={20} height={16} />
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg overflow-hidden border-[1px]">
              <div className="w-full h-72 border-b-[1px] relative">
                <img
                  src={module.thumbnail}
                  className="inset-0 object-cover w-full h-full absolute"
                />
                <div className="absolute inset-0" style={gridOverlayStyle} />
                <div className="absolute w-full h-72 bg-black opacity-30 text-white"></div>
                <div className="absolute w-full h-72 text-white p-4">
                  <div className="text-lg">
                    {module.episodes_count} Episodes •
                    {formatDuration(module.duration)}
                  </div>
                  <div className="md:w-1/2 w-2/3 text-5xl font-bold py-2">
                    {module.title}
                  </div>
                  <div className="flex items-center absolute bottom-8">
                    <div className="w-72 bg-gray-700 rounded-lg h-3">
                      <div
                        className="bg-gradient-text h-3 rounded-lg"
                        style={{
                          width: `${module.progress_bar.completion_percent}%`,
                        }}
                      ></div>
                    </div>
                    <div className="relative -left-[10px] w-10 h-10 rounded-full">
                      <img
                        src={module?.progress_bar?.badge_icon}
                        className={`w-full h-full ${module.progress_bar.completion_percent === 100 ? "" : "grayscale"}`}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                {episodes.map((episode) =>
                  renderEpisodes(
                    episode,
                    module?.module_id,
                    episode.badge_icon,
                  ),
                )}
              </div>
            </div>
          );
        })}
      </div>
      <RewardsComponent rewards={rewards} />
    </div>
  );
};

const RewardsComponent: React.FC<RewardsComponentProps> = ({ rewards }) => {
  const { modalName } = useAppSelector((state) => state.modal);
  return (
    <div className="w-1/4 border-2 border-red p-2 bg-white rounded-lg h-fit">
      <div className="flex gap-2 items-center p-2">
        <BadgeIcon width={18} height={24} />
        <h2 className="text-xl font-semibold">Rewards Earned</h2>
      </div>
      <div className="flex flex-wrapflex flex-wrap justify-start gap-4">
        {rewards?.map((reward) => (
          <img
            key={reward.module_id}
            src={reward.badge_icon}
            alt="Reward"
            className={`w-16 h-16 ${reward.is_completed ? "" : "grayscale-[100%]"}`}
          />
        ))}
      </div>
      {modalName === MODAL_NAME.BECOME_MEMBER && <BecomeMember />}
    </div>
  );
};

export default CourseComponent;
